import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { cloneDeep } from 'lodash';
import { TimeRangeKey, TimestampRange } from '../../components/time-picker/types';
import { TimeRange } from '../../models/time-range';
import { TimeRangeProvider } from '../values.exchange/time.range.provider';
import { ValuesExchangeProvider } from '../values.exchange/values.exchange.provider';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class TimePickerService {
  public default = false;
  lastTimeRange: TimeRange;
  constructor(
    private readonly timeProvider: TimeRangeProvider,
    private valuesExchange: ValuesExchangeProvider,
    private readonly userService: UserService
  ) {}

  replaceTimeRange(newRange: TimeRange, utc?: boolean): void {
    const isZoomed = sessionStorage.getItem('graphZoomed');
    const isAutoTimepicker = sessionStorage.getItem('autoTimepicker');
    if (isZoomed !== 'true' && isAutoTimepicker !== 'true') {
      this.lastTimeRange = cloneDeep(newRange);
    }
    let filledTimeRange;
    if ((utc && newRange.key === 'LAST_1H') || newRange.key === 'LAST_1H_CACHED') {
      newRange.from = null;
      newRange.to = null;
      filledTimeRange = newRange;
    } else {
      filledTimeRange = this.fillTimeRange(newRange);
    }
    this.timeProvider.replaceTimeRange(filledTimeRange);
  }

  setFixedTimeRange(fromTime: number, toTime: number, isZoomed?: boolean): void {
    const fixedTimeRange = this.makeFixedTimeRange(fromTime, toTime);
    fixedTimeRange['isZoomed'] = isZoomed;
    this.replaceTimeRange(fixedTimeRange);
  }

  replaceTimeRangeOnChartZoom(newRange: TimeRange, utc?: boolean, isZoomed = false): void {
    const isZoomedValue = isZoomed ? new Boolean(isZoomed).toString() : 'false';
    sessionStorage.setItem('graphZoomed', isZoomedValue);
    if (isZoomed) {
      this.lastTimeRange = cloneDeep(newRange);
    }
    if (newRange) {
      this.replaceTimeRange(newRange, utc);
    }
  }

  replaceTimeRangeOnAutoTimepicker(
    newRange: TimeRange,
    utc?: boolean,
    autoTimepicker = false
  ): void {
    const isAutoTimepicker = autoTimepicker ? new Boolean(autoTimepicker).toString() : 'false';
    sessionStorage.setItem('autoTimepicker', isAutoTimepicker);
    if (autoTimepicker) {
      this.lastTimeRange = cloneDeep(newRange);
    }
    if (newRange) {
      this.replaceTimeRange(newRange, utc);
    }
  }

  setFixedTimeRangeOnChartZoom(fromTime: number, toTime: number, isZoomed: boolean = false): void {
    const isZoomedValue = isZoomed ? String(isZoomed) : 'false';
    sessionStorage.setItem('graphZoomed', isZoomedValue);
    this.setFixedTimeRange(fromTime, toTime);
  }

  getActiveTimeRange$(): Observable<TimeRange> {
    return this.timeProvider.getActiveTimeRange$();
  }

  getCurrentValue(): TimeRange {
    return this.timeProvider.getCurrentValue();
  }

  public fillTimeRange(timeRange: TimeRange, from?: Date, to?: Date): TimeRange {
    // set time interval if provided
    if (from instanceof Date) {
      timeRange.fromTime = from.getTime();
    }
    if (to instanceof Date) {
      timeRange.toTime = to.getTime();
    }
    if (!timeRange.fromTime && !timeRange.toTime) {
      const toTime = Date.now();
      const fromTime = toTime - TimeRange.getTimerangeByKey(timeRange.key);
      timeRange.fromTime = fromTime;
      timeRange.toTime = toTime;
    }
    return timeRange;
  }

  createTimeRange(key: TimeRangeKey, fromTime?: Date | number, toTime?: Date | number): TimeRange {
    const from = fromTime ? (fromTime instanceof Date ? fromTime : new Date(fromTime)) : null;
    const to = toTime ? (toTime instanceof Date ? toTime : new Date(toTime)) : null;
    return this.fillTimeRange(this.timeProvider.getTemplate(key), from, to);
  }

  makeFixedTimeRange(fromTime: number, toTime: number): TimeRange {
    return this.timeProvider.makeFixedTimeRange(fromTime, toTime);
  }

  transformToFixedTimeRange(timeRangeValue: TimeRange): TimeRange {
    return this.timeProvider.transformToFixedTimeRange(timeRangeValue);
  }

  getSelectionAsFixed(): TimeRange {
    return this.transformToFixedTimeRange(this.getCurrentValue());
  }

  updateLast1HourRange(range: TimestampRange): void {
    const timeRange = this.getCurrentValue();

    if (!range || timeRange.key !== TimeRangeKey.LAST_1H) {
      return;
    }

    if (timeRange.fromTime !== range.fromTime || timeRange.toTime !== range.toTime) {
      this.replaceTimeRange(
        this.createTimeRange(TimeRangeKey.LAST_1H, range.fromTime, range.toTime)
      );
    }
  }

  // FUSION: setting default timerange
  setDefault(): void {
    this.default = true;
    const timeRange = this.createTimeRange(
      this.userService.isFusionDashboardProject()
        ? TimeRangeKey.LAST_1H_CACHED
        : TimeRangeKey.LAST_1H
    );
    timeRange.dimension = this.userService.isFusionDashboardProject()
      ? 'timestamp-by-minute-cached'
      : 'timestamp-by-minute';
    this.replaceTimeRange(timeRange, true);
  }
}
